import { useRef, useState } from 'react';
import { OptimusProvider, QueryBoundary, useConfirmationDialog } from '@mews-commander/ui';
import { FloatingPanel } from '@mews/b2b-ui';
import { LocalizedProperty, LocalizedPropertyObject, lt } from '@mews/framework';
import { Button } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { AddProductDialogProps, AddProductFormRef } from '../AddProductDialog';
import { ServiceOrderFormField } from '../schema';
import { TwoStepAddProductDialogContent } from './TwoStepAddProductDialogContent';
import { AddProductStep } from './constants';

export const TwoStepAddProductDialog = ({ onClose, active, ...props }: AddProductDialogProps) => {
    const [activeStep, setActiveStep] = useState(AddProductStep.Product);
    const currentFormRef = useRef<AddProductFormRef | null>(null);
    const [loading, setLoading] = useState(false);
    const [dialogName, setDialogName] = useState<LocalizedPropertyObject | null>(null);

    const { confirmationDialog, openConfirmationDialog } = useConfirmationDialog({
        onConfirm: onClose,
    });

    const handleSubmit = async () => {
        if (currentFormRef.current) {
            await currentFormRef.current?.submit();
        }
    };

    const handleNextClick = async () => {
        if (currentFormRef.current) {
            const result = await currentFormRef.current.validate([
                ServiceOrderFormField.Value,
                ServiceOrderFormField.Currency,
                ServiceOrderFormField.ProductOrders,
                ServiceOrderFormField.LinkedReservationId,
            ]);
            if (result) setActiveStep(AddProductStep.Settings);
        }
    };

    const handleClose = () => {
        const hasDirtyFields = currentFormRef?.current?.hasDirtyFields;

        if (hasDirtyFields) {
            openConfirmationDialog();
        } else {
            onClose();
        }
    };

    const ButtonComponent =
        activeStep === AddProductStep.Settings ? (
            <Button
                data-test-id="two-step-add-product-submit-button"
                variant="primary"
                onClick={handleSubmit}
                type="submit"
                loading={loading}
                disabled={loading}
            >
                {lt('Submit')}
            </Button>
        ) : (
            <Button
                data-test-id="two-step-add-product-next-button"
                variant="primary"
                onClick={handleNextClick}
                type="submit"
            >
                {lt('Next')}
            </Button>
        );

    return (
        <OptimusProvider density="spacious">
            <FloatingPanel
                size="medium"
                active={active}
                onClose={handleClose}
                title={
                    <>
                        {lt(TextKey.AddProduct)}
                        {dialogName && (
                            <>
                                {UnicodeEntity.Colon} <LocalizedProperty property={dialogName} />
                            </>
                        )}
                    </>
                }
                footer={{
                    primary: ButtonComponent,
                }}
            >
                <QueryBoundary loader="inline">
                    <TwoStepAddProductDialogContent
                        currentFormRef={currentFormRef}
                        setLoading={setLoading}
                        setDialogName={setDialogName}
                        activeStep={activeStep}
                        setActiveStep={setActiveStep}
                        onClose={onClose}
                        {...props}
                    />
                </QueryBoundary>
            </FloatingPanel>
            {confirmationDialog}
        </OptimusProvider>
    );
};
